// PCA-Abgleich: Vorschau der PCA-Standorte gegen dim.School, dann Übernehmen.
// Nichts passiert ohne Klick auf „Übernehmen“ (kein Zero-Click).
import { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Badge } from '../../shared/ui'
import { notify } from '../../store/notificationStore'
import { usePcaSchulenSyncPreview, usePcaSchulenSyncApply, type PcaSchulenSyncRow } from './api/schulenApi'

export default function PcaSyncDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const preview = usePcaSchulenSyncPreview(open)
  const apply   = usePcaSchulenSyncApply()
  const [picked, setPicked] = useState<Set<string>>(new Set())

  // Default: every actionable row selected; user can untick.
  useEffect(() => {
    if (!preview.data) return
    setPicked(new Set(preview.data.rows.filter(r => r.state !== 'linked').map(r => r.agencyId)))
  }, [preview.data])

  const groups = useMemo(() => {
    const g: Record<PcaSchulenSyncRow['state'], PcaSchulenSyncRow[]> = { match: [], new: [], linked: [] }
    for (const r of preview.data?.rows ?? []) g[r.state].push(r)
    return g
  }, [preview.data])

  const toggle = (id: string) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })
  const nLink   = groups.match.filter(r => picked.has(r.agencyId)).length
  const nCreate = groups.new.filter(r => picked.has(r.agencyId)).length

  const run = async () => {
    try {
      const res = await apply.mutateAsync({
        link:   groups.match.filter(r => picked.has(r.agencyId)).map(r => ({ agencyId: r.agencyId, fmSchoolId: r.fmSchoolId! })),
        create: groups.new.filter(r => picked.has(r.agencyId)).map(r => r.agencyId),
      })
      notify.success(`PCA-Abgleich: ${res.linked} verknüpft · ${res.created} Schulen angelegt · ${res.backfilled} Erlös-Zeilen zugeordnet`)
      onClose()
    } catch (e) {
      notify.error(`PCA-Abgleich fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  const Row = ({ r }: { r: PcaSchulenSyncRow }) => (
    <label className="flex items-center gap-3 px-3 py-2 border-b border-outline-variant/10 text-sm cursor-pointer hover:bg-surface-container-low">
      {r.state !== 'linked' && <input type="checkbox" checked={picked.has(r.agencyId)} onChange={() => toggle(r.agencyId)} />}
      <span className="flex-1">
        {r.name}
        <span className="ml-2 font-mono text-[11px] text-on-surface-variant">{r.shortName} · AGENCY_ID {r.agencyId} · {r.vertrag ?? '?'}</span>
      </span>
      {r.schoolName && <span className="text-on-surface-variant text-xs">→ {r.schoolName}</span>}
    </label>
  )

  const Section = ({ title, tone, rows, hint }: { title: string; tone: 'success' | 'warn' | 'neutral'; rows: PcaSchulenSyncRow[]; hint: string }) => (
    <div className="rounded-xl border border-outline-variant/30 overflow-hidden">
      <div className="px-3 py-2 bg-surface-container-low flex items-center gap-2">
        <Badge tone={tone}>{rows.length}</Badge>
        <span className="text-sm font-medium">{title}</span>
        <span className="text-[11px] text-on-surface-variant ml-auto">{hint}</span>
      </div>
      {rows.length === 0
        ? <div className="px-3 py-2 text-xs text-on-surface-variant">—</div>
        : <div className="max-h-56 overflow-auto">{rows.map(r => <Row key={r.agencyId} r={r} />)}</div>}
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="PCA-Abgleich"
      subtitle="PCA-Standorte (dbo.schulen) gegen unsere Schulen · schreibt fm.Schools + dim.School + Kennung"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" loading={apply.isPending} disabled={nLink + nCreate === 0 || preview.isLoading} onClick={run}>
            Übernehmen · {nLink} verknüpfen · {nCreate} anlegen
          </Button>
        </>
      }
    >
      {preview.isLoading && <div className="text-sm text-on-surface-variant">Lese PCA-Standorte…</div>}
      {preview.isError && <div className="text-sm text-error">Vorschau fehlgeschlagen: {String((preview.error as Error)?.message ?? preview.error)}</div>}
      {preview.data && (
        <div className="space-y-4">
          <Section title="Neu anlegen" tone="warn" rows={groups.new} hint="keine Schule mit diesem Namen → Schule + Kennung" />
          <Section title="Verknüpfen" tone="success" rows={groups.match} hint="Namensabgleich trifft → nur Kennung" />
          <Section title="Bereits verknüpft" tone="neutral" rows={groups.linked} hint="Kennung vorhanden" />
        </div>
      )}
    </Modal>
  )
}
